/**
 * Panel callbacks -> tse:action {kind: "command", action, params} (tse_action.schema.json command_names).
 * The panel never changes state itself; it waits for the next tse:diag_state (15.3).
 * Dev-only POC callbacks (onSettings, onHotline) have no command and are dropped.
 */

export const COMMANDS = Object.freeze({
  FOCUS_LOD: "focus_lod",
  OVERRIDE_CAUSE: "override_cause",
  UNDO_OVERRIDE: "undo_override",
  TEACH: "teach",
  LEARNING_TOGGLE: "learning_toggle",
  SWITCH_TO_AUTOMATIC: "switch_to_automatic",
  DISMISS_DIAGNOSIS_PROMPT: "dismiss_diagnosis_prompt",
  HAND_OVER: "hand_over",
  KEEP_LOOKING: "keep_looking",
});

const CAUSE_KEY_RX = /^PC_[0-9]+$/;
const MAX_CAUSE_TEXT = 200;

/**
 * @param {string} callback the POC callback name (onOverride, onUndoOverride, onFocusLod, onTeach, onLearning)
 * @param {Array} args the callback's arguments as the POC fires them
 * @param {{focusedLod?: string|null}} [ctx] current diag_state facts a command needs
 * @returns {{action: string, params: object}|null} null when the callback has no command (or bad input)
 */
export function commandFromPanel(callback, args = [], ctx = {}) {
  switch (callback) {
    case "onOverride": {
      const [causeKey] = args;
      return CAUSE_KEY_RX.test(String(causeKey || "")) ? { action: COMMANDS.OVERRIDE_CAUSE, params: { cause_key: causeKey } } : null;
    }
    case "onUndoOverride":
      return { action: COMMANDS.UNDO_OVERRIDE, params: {} };
    case "onFocusLod": {
      const [lodId] = args;
      if (lodId) return { action: COMMANDS.FOCUS_LOD, params: { lod_id: String(lodId) } };
      // "Check all lines": the contract has no unfocus command yet, so the focused line is sent with clear: true (W7b open question).
      return ctx.focusedLod ? { action: COMMANDS.FOCUS_LOD, params: { lod_id: String(ctx.focusedLod), clear: true } } : null;
    }
    case "onTeach": {
      const [causeKey, newCause] = args;
      if (CAUSE_KEY_RX.test(String(causeKey || ""))) return { action: COMMANDS.TEACH, params: { cause_key: causeKey } };
      const text = String(newCause || "").trim().slice(0, MAX_CAUSE_TEXT);
      return text ? { action: COMMANDS.TEACH, params: { cause_text: text } } : null;
    }
    case "onLearning": {
      const [enabled] = args;
      return { action: COMMANDS.LEARNING_TOGGLE, params: { enabled: !!enabled } };
    }
    default:
      return null;
  }
}

/** The two diagnosis-prompt buttons (tse_diagnosis_prompt.schema.json): both carry params.prompt_id. */
export function promptCommand(prompt, confirm) {
  if (!prompt?.id) return null;
  const action = confirm ? (prompt.confirm_command || COMMANDS.SWITCH_TO_AUTOMATIC) : (prompt.dismiss_command || COMMANDS.DISMISS_DIAGNOSIS_PROMPT);
  return { action, params: { prompt_id: prompt.id } };
}
